// The game's numbers that the admin dashboard can change without code: fees, the power hour, stake limits, XP,
// weekly prizes, referrals and challenge timers. The server keeps every saved set as a version and loads the live
// one; the Mini App gets the same set from /v1/status. Fees are fixed on a battle when it starts, so a change never
// touches a battle already running.

export interface StakeTier {
  /** The largest stake this tier allows, in USDT. */
  maxStake: number;
  /** Battles played to reach it. */
  battles: number;
  /** Level to reach it. */
  level: number;
}

export interface GameRules {
  /** The battle fee, in hundredths of a percent of the pot: 400 is 4%. */
  feeBps: number;
  powerHour: {
    on: boolean;
    /** "HH:MM" in West Africa Time. */
    startWat: string;
    minutes: number;
    /** The fee for battles that start inside the hour. */
    feeBps: number;
  };
  /** The smallest stake, in USDT. */
  minStake: number;
  /** Stake limits, lowest first. The first is where everyone starts. */
  tiers: StakeTier[];
  xp: { win: number; streakStep: number; streakMax: number; draw: number; loss: number; pairDaily: number };
  weekly: { potPct: number; xpSharePct: number; minBattles: number; minOpponents: number };
  referral: { sharePct: number; freeWins: number };
  challenge: { linkMin: number; rematchSec: number; noThanksMin: number; revengeGapMin: number };
}

export const DEFAULT_RULES: GameRules = {
  feeBps: 400,
  powerHour: { on: true, startWat: "19:00", minutes: 60, feeBps: 200 },
  minStake: 2,
  tiers: [
    { maxStake: 50, battles: 0, level: 1 },
    { maxStake: 250, battles: 20, level: 1 },
    { maxStake: 1000, battles: 100, level: 10 },
  ],
  xp: { win: 100, streakStep: 10, streakMax: 50, draw: 40, loss: 25, pairDaily: 5 },
  weekly: { potPct: 10, xpSharePct: 60, minBattles: 10, minOpponents: 3 },
  referral: { sharePct: 20, freeWins: 5 },
  challenge: { linkMin: 5, rematchSec: 60, noThanksMin: 2, revengeGapMin: 10 },
};

/** The most a stake can ever be: the vault and the risk checks are built for it. */
export const STAKE_CEILING = 1000;

let live: GameRules = DEFAULT_RULES;

/** The rules in force now. Defaults until the server (or, in the app, /v1/status) says otherwise. */
export function rules(): GameRules {
  return live;
}

/** Puts a set of rules in force for this process. */
export function useRules(r: GameRules): void {
  live = r;
}

/** Fills anything a stored set is missing (a rule added since it was saved) from the defaults. */
export function withDefaults(r: Partial<GameRules> | null | undefined): GameRules {
  const d = DEFAULT_RULES;
  const x = (r ?? {}) as Partial<GameRules>;
  return {
    feeBps: x.feeBps ?? d.feeBps,
    powerHour: { ...d.powerHour, ...x.powerHour },
    minStake: x.minStake ?? d.minStake,
    tiers: x.tiers?.length ? x.tiers : d.tiers,
    xp: { ...d.xp, ...x.xp },
    weekly: { ...d.weekly, ...x.weekly },
    referral: { ...d.referral, ...x.referral },
    challenge: { ...d.challenge, ...x.challenge },
  };
}

/** "4%", "2.5%". */
export function pct(bps: number): string {
  return `${Number((bps / 100).toFixed(2))}%`;
}

/** The power hour as players read it: "19:00–20:00 WAT". */
export function powerHourLabel(r: GameRules = live): string {
  const [h, m] = r.powerHour.startWat.split(":").map(Number) as [number, number];
  const end = (h * 60 + m + r.powerHour.minutes) % 1440;
  return `${r.powerHour.startWat}–${String(Math.floor(end / 60)).padStart(2, "0")}:${String(end % 60).padStart(2, "0")} WAT`;
}

/** The power hour's start in UTC ("18:00" for 19:00 WAT), or "" when it's off. */
export function powerHourUtc(r: GameRules = live): string {
  if (!r.powerHour.on) return "";
  const [h, m] = r.powerHour.startWat.split(":").map(Number) as [number, number];
  const utc = (h * 60 + m - 60 + 1440) % 1440;
  return `${String(Math.floor(utc / 60)).padStart(2, "0")}:${String(utc % 60).padStart(2, "0")}`;
}

const int = (v: unknown, lo: number, hi: number) => typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi;

/** What's wrong with a set of rules, in words, or null when it can be saved. */
export function rulesProblem(r: GameRules): string | null {
  if (!int(r.feeBps, 0, 1000)) return "The battle fee must be between 0% and 10%.";
  if (!int(r.powerHour.feeBps, 0, r.feeBps)) return "The power hour fee can't be more than the battle fee.";
  if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(r.powerHour.startWat)) return "The power hour time must look like 19:00.";
  if (!int(r.powerHour.minutes, 15, 240)) return "The power hour must last 15 to 240 minutes.";
  if (!int(r.minStake, 1, 100)) return "The smallest stake must be 1 to 100 USDT.";
  if (r.tiers.length < 1 || r.tiers.length > 5) return "There must be 1 to 5 stake tiers.";
  for (const [i, t] of r.tiers.entries()) {
    if (!int(t.maxStake, r.minStake, STAKE_CEILING)) return `Each tier's stake must be between the smallest stake and ${STAKE_CEILING} USDT.`;
    if (!int(t.battles, 0, 100_000) || !int(t.level, 1, 1000)) return "Tier battles and levels must be whole numbers.";
    const prev = r.tiers[i - 1];
    if (i === 0 && (t.battles !== 0 || t.level !== 1)) return "The first tier is where everyone starts: 0 battles, level 1.";
    if (prev && (t.maxStake <= prev.maxStake || t.battles < prev.battles || t.level < prev.level)) return "Each tier must allow more than the one before it, and need at least as much.";
  }
  const { win, streakStep, streakMax, draw, loss, pairDaily } = r.xp;
  if (![win, streakStep, streakMax, draw, loss].every((v) => int(v, 0, 1000))) return "XP values must be 0 to 1,000.";
  if (!int(pairDaily, 1, 50)) return "Battles a day per opponent must be 1 to 50.";
  if (!int(r.weekly.potPct, 0, 50)) return "The weekly pot must be 0% to 50% of fees.";
  if (!int(r.weekly.xpSharePct, 0, 100)) return "The Weekly XP share must be 0% to 100%.";
  if (!int(r.weekly.minBattles, 0, 1000) || !int(r.weekly.minOpponents, 1, 100)) return "The rules to place on a board must be whole numbers.";
  if (!int(r.referral.sharePct, 0, 50)) return "The inviter share must be 0% to 50% of the fee.";
  if (!int(r.referral.freeWins, 0, 50)) return "Fee-free wins must be 0 to 50.";
  if (!int(r.challenge.linkMin, 1, 60) || !int(r.challenge.noThanksMin, 0, 60) || !int(r.challenge.revengeGapMin, 1, 1440)) return "Challenge times must be whole minutes.";
  if (!int(r.challenge.rematchSec, 15, 600)) return "The rematch offer must be open 15 to 600 seconds.";
  return null;
}

/** Changing these waits 24 hours, so players hear about a fee change before it happens. */
export function feesChanged(a: GameRules, b: GameRules): boolean {
  return a.feeBps !== b.feeBps || a.powerHour.feeBps !== b.powerHour.feeBps || a.referral.freeWins !== b.referral.freeWins;
}

const tierText = (t: StakeTier[]) => t.map((x) => `${x.maxStake} at ${x.battles} battles${x.level > 1 ? `, level ${x.level}` : ""}`).join("; ");

/** Every rule with its name and how it reads, for the change summary and the audit log. */
export const RULE_FIELDS: { label: string; get: (r: GameRules) => string }[] = [
  { label: "Battle fee", get: (r) => pct(r.feeBps) },
  { label: "Power hour", get: (r) => (r.powerHour.on ? powerHourLabel(r) : "off") },
  { label: "Power hour fee", get: (r) => pct(r.powerHour.feeBps) },
  { label: "Smallest stake", get: (r) => `${r.minStake} USDT` },
  { label: "Stake tiers", get: (r) => tierText(r.tiers) },
  { label: "Win XP", get: (r) => String(r.xp.win) },
  { label: "Streak step", get: (r) => String(r.xp.streakStep) },
  { label: "Streak bonus cap", get: (r) => String(r.xp.streakMax) },
  { label: "Draw XP", get: (r) => String(r.xp.draw) },
  { label: "Loss XP", get: (r) => String(r.xp.loss) },
  { label: "XP battles a day per opponent", get: (r) => String(r.xp.pairDaily) },
  { label: "Weekly pot", get: (r) => `${r.weekly.potPct}%` },
  { label: "Weekly XP / High rollers split", get: (r) => `${r.weekly.xpSharePct} / ${100 - r.weekly.xpSharePct}` },
  { label: "To place on a board", get: (r) => `${r.weekly.minBattles} battles, ${r.weekly.minOpponents} opponents` },
  { label: "Inviter share", get: (r) => `${r.referral.sharePct}%` },
  { label: "Fee-free wins", get: (r) => String(r.referral.freeWins) },
  { label: "Challenge link open", get: (r) => `${r.challenge.linkMin} min` },
  { label: "Rematch offer", get: (r) => `${r.challenge.rematchSec} s` },
  { label: "Wait after No thanks", get: (r) => `${r.challenge.noThanksMin} min` },
  { label: "Revenge message gap", get: (r) => `${r.challenge.revengeGapMin} min` },
];

/** The next stake tier a player hasn't reached, and what it still needs, or null at the top. */
export function nextTier(battles: number, level: number, r: GameRules = live): { tier: StakeTier; battles: number; levelNeeded: number | null } | null {
  const reached = r.tiers.filter((t) => battles >= t.battles && level >= t.level).reduce((m, t) => Math.max(m, t.maxStake), r.tiers[0]!.maxStake);
  const tier = r.tiers.find((t) => t.maxStake > reached);
  if (!tier) return null;
  return { tier, battles: Math.max(0, tier.battles - battles), levelNeeded: level < tier.level ? tier.level : null };
}

/** "1K", "250". */
export function shortUsdt(n: number): string {
  return n >= 1000 && n % 1000 === 0 ? `${n / 1000}K` : n.toLocaleString("en-US");
}

/** "Battle fee 4% to 3%, Win XP 100 to 120". */
export function describeChanges(from: GameRules, to: GameRules): string[] {
  return RULE_FIELDS.filter((f) => f.get(from) !== f.get(to)).map((f) => `${f.label} ${f.get(from)} to ${f.get(to)}`);
}
